import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

// Common BigQuery regions for quick add
const COMMON_REGIONS = [
  { regionId: 'asia-southeast2', name: 'Jakarta, Indonesia', flag: '🇮🇩' },
  { regionId: 'asia-southeast1', name: 'Singapore', flag: '🇸🇬' },
  { regionId: 'asia-east1', name: 'Taiwan', flag: '🇹🇼' },
  { regionId: 'asia-northeast1', name: 'Tokyo, Japan', flag: '🇯🇵' },
  { regionId: 'asia-northeast2', name: 'Osaka, Japan', flag: '🇯🇵' },
  { regionId: 'asia-northeast3', name: 'Seoul, South Korea', flag: '🇰🇷' },
  { regionId: 'asia-south1', name: 'Mumbai, India', flag: '🇮🇳' },
  { regionId: 'australia-southeast1', name: 'Sydney, Australia', flag: '🇦🇺' },
  { regionId: 'US', name: 'United States (Multi-region)', flag: '🇺🇸' },
  { regionId: 'EU', name: 'European Union (Multi-region)', flag: '🇪🇺' },
  { regionId: 'us-central1', name: 'Iowa, USA', flag: '🇺🇸' },
  { regionId: 'us-east1', name: 'South Carolina, USA', flag: '🇺🇸' },
  { regionId: 'us-west1', name: 'Oregon, USA', flag: '🇺🇸' },
  { regionId: 'europe-west1', name: 'Belgium', flag: '🇧🇪' },
  { regionId: 'europe-west2', name: 'London, UK', flag: '🇬🇧' },
  { regionId: 'europe-west3', name: 'Frankfurt, Germany', flag: '🇩🇪' }
];

function RegionModal({ isOpen, onClose, onSuccess, editRegion = null }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    regionId: '',
    name: '',
    description: '',
    flag: '',
    isActive: true,
    isDefault: false,
    sortOrder: 0,
    apiEndpoint: '',
    whitelistIps: ''
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  useEffect(() => {
    if (editRegion) {
      setFormData({
        regionId: editRegion.regionId || '',
        name: editRegion.name || '',
        description: editRegion.description || '',
        flag: editRegion.flag || '',
        isActive: editRegion.isActive !== false,
        isDefault: editRegion.isDefault || false,
        sortOrder: editRegion.sortOrder || 0,
        apiEndpoint: editRegion.apiEndpoint || '',
        whitelistIps: Array.isArray(editRegion.whitelistIps) 
          ? editRegion.whitelistIps.join(', ') 
          : (editRegion.whitelistIps || '')
      });
    } else {
      setFormData({
        regionId: '',
        name: '',
        description: '',
        flag: '',
        isActive: true,
        isDefault: false,
        sortOrder: 0,
        apiEndpoint: '',
        whitelistIps: ''
      });
    }
  }, [editRegion, isOpen]);
  
  const handleSelectSuggestion = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      regionId: suggestion.regionId,
      name: suggestion.name,
      flag: suggestion.flag
    }));
    setShowSuggestions(false);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Process whitelistIps into array
      const processedData = {
        ...formData,
        whitelistIps: formData.whitelistIps
          ? formData.whitelistIps.split(',').map(ip => ip.trim()).filter(ip => ip)
          : []
      };
      
      if (editRegion) {
        await adminApi.updateRegion(editRegion._id, processedData);
        toast.success('Region updated successfully!');
      } else {
        await adminApi.createRegion(processedData);
        toast.success('Region created successfully!');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to ${editRegion ? 'update' : 'create'} region`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-100">
              {editRegion ? 'Edit Region' : 'Add New Region'}
            </Dialog.Title>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Quick Select from Common Regions */}
            {!editRegion && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowSuggestions(!showSuggestions)}
                  className="btn-secondary w-full justify-between"
                >
                  <span>Select from common regions</span>
                  <span className="text-xs text-surface-400">{showSuggestions ? '▲' : '▼'}</span>
                </button>
                
                {showSuggestions && (
                  <div className="absolute z-10 mt-1 w-full bg-surface-800 border border-surface-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {COMMON_REGIONS.map((region) => (
                      <button
                        key={region.regionId}
                        type="button"
                        onClick={() => handleSelectSuggestion(region)}
                        className="w-full px-3 py-2 text-left hover:bg-surface-700 flex items-center gap-2 text-surface-200"
                      >
                        <span>{region.flag}</span>
                        <span className="flex-1">{region.name}</span>
                        <span className="text-xs text-surface-400">{region.regionId}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Region ID *
              </label>
              <input
                type="text"
                value={formData.regionId}
                onChange={(e) => setFormData(p => ({ ...p, regionId: e.target.value }))}
                className="input"
                placeholder="e.g., asia-southeast2"
                required
                disabled={!!editRegion}
              />
              {editRegion && (
                <p className="text-xs text-surface-400 mt-1">Region ID cannot be changed after creation</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Display Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                className="input"
                placeholder="e.g., Jakarta, Indonesia"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Flag (emoji)
                </label>
                <input
                  type="text"
                  value={formData.flag}
                  onChange={(e) => setFormData(p => ({ ...p, flag: e.target.value }))}
                  className="input"
                  placeholder="e.g., 🇮🇩"
                  maxLength={10}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData(p => ({ ...p, sortOrder: parseInt(e.target.value) || 0 }))}
                  className="input"
                  min="0"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                className="input"
                placeholder="Optional description for this region"
                rows={2}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                API Endpoint URL
                <span className="text-surface-500 font-normal ml-2">(Production Only)</span>
              </label>
              <input
                type="url"
                value={formData.apiEndpoint}
                onChange={(e) => setFormData(p => ({ ...p, apiEndpoint: e.target.value }))}
                className="input"
                placeholder="https://neoshift-backend-xxx.run.app/api"
              />
              <p className="text-xs text-surface-400 mt-1">
                Cloud Run regional endpoint for this region. Leave empty for staging/local environments.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Whitelist IP Addresses
                <span className="text-surface-500 font-normal ml-2">(For Database Imports)</span>
              </label>
              <textarea
                value={formData.whitelistIps}
                onChange={(e) => setFormData(p => ({ ...p, whitelistIps: e.target.value }))}
                className="input"
                placeholder="34.101.xxx.xxx, 35.xxx.xxx.xxx (comma-separated)"
                rows={3}
              />
              <p className="text-xs text-surface-400 mt-1">
                NAT IP addresses that users should whitelist in their firewall when importing from PostgreSQL, MySQL, or MongoDB. Separate multiple IPs with commas.
              </p>
            </div>
            
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(p => ({ ...p, isActive: e.target.checked }))}
                  className="w-4 h-4 rounded border-surface-600 bg-surface-700 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm text-surface-300">Active</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData(p => ({ ...p, isDefault: e.target.checked }))}
                  className="w-4 h-4 rounded border-surface-600 bg-surface-700 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm text-surface-300">Default Region</span>
              </label>
            </div>
            
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Saving...' : (editRegion ? 'Update Region' : 'Add Region')}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function RegionRow({ region, onEdit, onDelete, onSetDefault }) {
  const [deleting, setDeleting] = useState(false);
  
  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete the region "${region.name}"?`)) {
      return;
    }
    
    setDeleting(true);
    try {
      await onDelete(region._id);
    } finally {
      setDeleting(false);
    }
  };
  
  return (
    <tr className="border-b border-surface-700 hover:bg-surface-800/50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">{region.flag || '🌍'}</span>
          <div>
            <p className="font-medium text-surface-100">{region.name}</p>
            <p className="text-xs text-surface-400">{region.regionId}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {region.description ? (
          <p className="text-sm text-surface-300 max-w-xs truncate">{region.description}</p>
        ) : (
          <span className="text-surface-500">-</span>
        )}
      </td>
      <td className="px-4 py-3">
        {region.apiEndpoint ? (
          <p className="text-sm text-surface-300 max-w-xs truncate font-mono" title={region.apiEndpoint}>
            {region.apiEndpoint}
          </p>
        ) : (
          <span className="text-surface-500 text-sm">Not configured</span>
        )}
      </td>
      <td className="px-4 py-3">
        {region.whitelistIps && region.whitelistIps.length > 0 ? (
          <div className="max-w-xs">
            <p className="text-sm text-surface-300 font-mono truncate" 
               title={Array.isArray(region.whitelistIps) ? region.whitelistIps.join(', ') : region.whitelistIps}>
              {Array.isArray(region.whitelistIps) ? region.whitelistIps.join(', ') : region.whitelistIps}
            </p>
          </div>
        ) : (
          <span className="text-surface-500 text-sm">No IPs configured</span>
        )}
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-surface-300">{region.sortOrder}</span>
      </td>
      <td className="px-4 py-3">
        {region.isActive ? (
          <span className="badge-success">
            <CheckCircleIcon className="w-3 h-3 mr-1" /> Active
          </span>
        ) : (
          <span className="badge-danger">
            <XCircleIcon className="w-3 h-3 mr-1" /> Inactive
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-center">
        {region.isDefault ? (
          <StarIconSolid className="w-5 h-5 text-amber-400 mx-auto" />
        ) : (
          <button
            onClick={() => onSetDefault(region._id)}
            className="text-surface-500 hover:text-amber-400 transition-colors mx-auto block"
            title="Set as default"
          >
            <StarIcon className="w-5 h-5" />
          </button>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={() => onEdit(region)}
            className="btn-ghost btn-sm"
            title="Edit"
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="btn-ghost btn-sm text-red-400 hover:text-red-300"
            title="Delete"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function Regions() {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editRegion, setEditRegion] = useState(null);
  
  useEffect(() => {
    loadRegions();
  }, []);
  
  const loadRegions = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getRegions();
      setRegions(response.data.regions || []);
    } catch (error) {
      toast.error('Failed to load regions');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEdit = (region) => {
    setEditRegion(region);
    setShowModal(true);
  };
  
  const handleDelete = async (id) => {
    try {
      await adminApi.deleteRegion(id);
      toast.success('Region deleted successfully');
      loadRegions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete region');
    }
  };
  
  const handleSetDefault = async (id) => {
    try {
      await adminApi.setDefaultRegion(id);
      toast.success('Default region updated');
      loadRegions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to set default region');
    }
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
    setEditRegion(null);
  };
  
  const filteredRegions = regions.filter(region =>
    region.name.toLowerCase().includes(search.toLowerCase()) ||
    region.regionId.toLowerCase().includes(search.toLowerCase())
  );
  
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">BigQuery Regions</h1>
          <p className="text-surface-400 mt-1">Manage available data warehouse regions for companies</p>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-primary">
          <PlusIcon className="w-5 h-5" />
          Add Region
        </button>
      </div>
      
      {/* Search */}
      <div className="card">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
          <input
            type="text"
            placeholder="Search regions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>
      
      {/* Regions Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : regions.length === 0 ? (
        <div className="card text-center py-12">
          <GlobeAltIcon className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-300 mb-2">No regions configured</h3>
          <p className="text-surface-400 mb-6">Add BigQuery regions that companies can select for their data warehouse</p>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            <PlusIcon className="w-5 h-5" />
            Add Region
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full">
            <thead className="bg-surface-800">
              <tr className="text-left text-sm text-surface-400">
                <th className="px-4 py-3 font-medium">Region</th>
                <th className="px-4 py-3 font-medium">Description</th>
                <th className="px-4 py-3 font-medium">API Endpoint</th>
                <th className="px-4 py-3 font-medium">Whitelist IPs</th>
                <th className="px-4 py-3 font-medium text-center">Order</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-center">Default</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRegions.map((region) => (
                <RegionRow
                  key={region._id}
                  region={region}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onSetDefault={handleSetDefault}
                />
              ))}
            </tbody>
          </table>
          
          {filteredRegions.length === 0 && search && (
            <div className="text-center py-8 text-surface-400">
              No regions found matching "{search}"
            </div>
          )}
        </div>
      )}
      
      <RegionModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onSuccess={loadRegions}
        editRegion={editRegion}
      />
    </div>
  );
}
